import { CommonModule, Location } from '@angular/common';
import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowLeft,
  lucideRefreshCw,
  lucideExternalLink,
  lucideBanknote,
  lucideBuilding2,
  lucideStore,
  lucideClipboard,
  lucideTriangleAlert,
  lucideSend,
  lucideLayers,
  lucideShieldCheck,
  lucideLayoutGrid,
  lucideWallet,
  lucideX,
} from '@ng-icons/lucide';

import type { EntityConfig } from '../../config/entity-config';
import { UI } from '../../components/ui';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { goBackOrNavigate } from '../../utils/navigation';
import { MerchantApplicationService, type ScopeChoice } from '../../services/merchant-application.service';
import { TransactionHubService, type TransactionHubPayload } from '../../services/transaction-hub.service';
import { WebhookService, type RetryResponse, type WebhookEvent } from '../../services/webhook.service';
import { FeeAnalyticsService, type FeeTransactionRow } from '../../services/fee-analytics.service';
import { formatHumanAmount } from '../../utils/admin-formatting';

/**
 * Transaction hub: themed layout, shortcuts, and webhook actions via ``WebhookService``
 * (``manage/webhook-events/{id}/resend|retry``).
 */
@Component({
  selector: 'app-transaction-hub',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, NgIcon, ...UI],
  templateUrl: './transaction-hub.component.html',
  viewProviders: [
    provideIcons({
      lucideArrowLeft,
      lucideRefreshCw,
      lucideExternalLink,
      lucideBanknote,
      lucideBuilding2,
      lucideStore,
      lucideClipboard,
      lucideTriangleAlert,
      lucideSend,
      lucideLayers,
      lucideShieldCheck,
      lucideLayoutGrid,
      lucideWallet,
      lucideX,
    }),
  ],
})
export class TransactionHubComponent implements OnInit {
  @Input() entityConfig!: EntityConfig;
  @Input() id!: string;

  private readonly hubApi = inject(TransactionHubService);
  private readonly webhookApi = inject(WebhookService);
  private readonly feeAnalytics = inject(FeeAnalyticsService);
  private readonly merchantApplicationApi = inject(MerchantApplicationService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  readonly shellRoutes = inject(ShellRoutesService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly hub = signal<TransactionHubPayload | null>(null);
  readonly feeRows = signal<FeeTransactionRow[]>([]);
  readonly actionBusy = signal(false);
  readonly rowBusy = signal<string | null>(null);
  readonly refundModalOpen = signal(false);
  readonly refundError = signal<string | null>(null);
  refundReason = '';
  refundPassword = '';
  refundAmount = '';
  refundCode = 'CUSTOMER_REQUEST';

  readonly tab = signal<'details' | 'webhooks'>('details');
  readonly openWebhookId = signal<string | null>(null);
  readonly webhookDetail = signal<WebhookEvent | null>(null);
  readonly webhookDetailLoading = signal(false);
  readonly webhookDetailError = signal<string | null>(null);

  readonly scopesEditing = signal(false);
  readonly scopesSaving = signal(false);
  readonly scopesError = signal<string | null>(null);
  editAllowedScopes: string[] = [];

  ngOnInit(): void {
    this.load();
  }

  goBack(): void {
    goBackOrNavigate(this.location, this.router, this.shellRoutes.commands('manage', this.entityConfig.key));
  }

  load(): void {
    if (!this.entityConfig?.listPath || !this.id) {
      this.loading.set(false);
      this.error.set('Missing entity or id.');
      return;
    }
    this.loading.set(true);
    this.error.set(null);
    this.hubApi.getHub(this.entityConfig.listPath, this.id).subscribe({
      next: (data) => {
        this.hub.set(data);
        this.feeAnalytics.getTransactionBreakdown({ period: 'month', transaction_id: this.id }).subscribe({
          next: (res) => this.feeRows.set(res.rows),
          error: () => this.feeRows.set([]),
        });
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.detail ?? err?.message ?? 'Failed to load hub.');
        this.loading.set(false);
      },
    });
  }

  get rootKey(): 'transaction' | 'mandate' | 'batch' {
    const k = this.entityConfig.key;
    if (k === 'direct-debits') {
      return 'mandate';
    }
    if (k === 'batch-disbursements') {
      return 'batch';
    }
    return 'transaction';
  }

  rootPayload(): Record<string, unknown> | null {
    const h = this.hub();
    if (!h) {
      return null;
    }
    const key = this.rootKey;
    const v = h[key];
    return v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
  }

  hubWallet(): Record<string, unknown> | null {
    const w = this.hub()?.['wallet'];
    return w && typeof w === 'object' ? (w as Record<string, unknown>) : null;
  }

  depositRefundSummary(): { amount: string; currency: string } | null {
    if (this.entityConfig.key !== 'deposits') {
      return null;
    }
    const r = this.rootPayload();
    if (!r) {
      return null;
    }
    const cur = r['currency_code'] ?? r['currency_name'];
    return {
      amount: this.str(r['amount']),
      currency: this.str(typeof cur === 'string' || typeof cur === 'number' ? cur : ''),
    };
  }

  canCheckStatus(): boolean {
    const supportedEntities = new Set([
      'deposits',
      'payouts',
      'refunds',
      'remittances',
      'direct-debits',
    ]);
    if (!supportedEntities.has(this.entityConfig.key)) {
      console.log('Unsupported entity:', this.entityConfig.key);
      return false;
    }
    const root = this.rootPayload();
    // Status value mode can be PENDING, PROCESSING, SUCCESS, FAILED or {value: 'PENDING', title: 'Pending'}
    const statusValue = typeof root?.['status'] === 'object' ? (root?.['status'] as {value: string, title: string}).value?.toUpperCase() : this.str(root?.['status'] ?? '').toUpperCase();
    console.log('Status value:', statusValue);
    return statusValue === 'PENDING';
  }

  /** What can still be refunded on this deposit (pending refunds reserve their amount). */
  refundableAmount(): string {
    const value = this.rootPayload()?.['refundable_amount'];
    return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  }

  refundedAmount(): string {
    const value = this.rootPayload()?.['refund_amount'];
    return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  }

  /** The refund bar shows unless the deposit is known to have nothing left to refund. */
  canRefundDeposit(): boolean {
    const root = this.rootPayload();
    if (root?.['can_be_refunded'] === false) {
      return false;
    }
    const left = this.refundableAmount();
    return left === '' || Number(left) > 0;
  }

  openRefundModal(): void {
    this.refundReason = '';
    this.refundPassword = '';
    this.refundAmount = this.refundableAmount() || this.depositRefundSummary()?.amount || '';
    this.refundCode = 'CUSTOMER_REQUEST';
    this.refundError.set(null);
    this.error.set(null);
    this.refundModalOpen.set(true);
  }

  closeRefundModal(): void {
    this.refundModalOpen.set(false);
    this.refundPassword = '';
    this.refundError.set(null);
  }

  merchantBlock(): Record<string, unknown> | null {
    const m = this.hub()?.['merchant'];
    return m && typeof m === 'object' ? (m as Record<string, unknown>) : null;
  }

  applicationBlock(): Record<string, unknown> | null {
    const a = this.hub()?.['application'];
    return a && typeof a === 'object' ? (a as Record<string, unknown>) : null;
  }

  scopeChoicesHub(): ScopeChoice[] {
    const app = this.applicationBlock();
    const raw = app?.['allowed_scope_choices'];
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.filter(
      (x): x is ScopeChoice =>
        x !== null &&
        typeof x === 'object' &&
        typeof (x as ScopeChoice).value === 'string' &&
        typeof (x as ScopeChoice).label === 'string',
    );
  }

  scopeLabelHub(value: string): string {
    const found = this.scopeChoicesHub().find((c) => c.value === value);
    return found?.label ?? value;
  }

  startEditScopes(): void {
    const app = this.applicationBlock();
    const scopes = app?.['allowed_scopes'];
    this.editAllowedScopes = Array.isArray(scopes) ? [...scopes.map((x) => String(x))] : [];
    this.scopesError.set(null);
    this.scopesEditing.set(true);
  }

  cancelEditScopes(): void {
    this.scopesEditing.set(false);
  }

  toggleScopeHub(scope: string): void {
    const ALL = 'all_scopes';
    if (scope === ALL) {
      if (this.editAllowedScopes.includes(ALL)) {
        this.editAllowedScopes = [];
      } else {
        this.editAllowedScopes = [ALL];
      }
      return;
    }
    let next = [...this.editAllowedScopes].filter((s) => s !== ALL);
    if (next.includes(scope)) {
      next = next.filter((s) => s !== scope);
    } else {
      next.push(scope);
    }
    this.editAllowedScopes = next;
  }

  isScopeSelectedHub(scope: string): boolean {
    return this.editAllowedScopes.includes(scope);
  }

  allowedScopesDisplay(): string[] {
    const app = this.applicationBlock();
    const s = app?.['allowed_scopes'];
    if (!Array.isArray(s)) {
      return [];
    }
    return s.map((x) => String(x));
  }

  saveScopesHub(): void {
    const app = this.applicationBlock();
    const id = app?.['id'];
    if (!id || typeof id !== 'string') {
      return;
    }
    this.scopesSaving.set(true);
    this.scopesError.set(null);
    this.merchantApplicationApi.update(id, { allowed_scopes: this.editAllowedScopes }).subscribe({
      next: () => {
        this.scopesSaving.set(false);
        this.scopesEditing.set(false);
        this.load();
      },
      error: (err: { error?: { detail?: string }; message?: string }) => {
        this.scopesError.set(err?.error?.detail ?? err?.message ?? 'Failed to save scopes.');
        this.scopesSaving.set(false);
      },
    });
  }

  str(v: unknown): string {
    return v === null || v === undefined ? '' : String(v);
  }


  /** Fields hidden from the grouped blocks (internal ids, duplicated composites, rendered elsewhere). */
  private static readonly HIDDEN_FIELDS = new Set([
    'id',
    'references',
    'application',
    'application_id',
    'merchant_id',
    'currency',
    'metadata',
    'is_terminal',
    'is_successful',
  ]);

  private static readonly FIELD_GROUPS: { title: string; keys: string[] }[] = [
    {
      title: 'References',
      keys: [
        'internal_reference',
        'external_reference',
        'merchant_reference',
        'provider_transaction_id',
        'description',
        'batch_reference',
        'original_deposit_reference',
      ],
    },
    {
      title: 'Parties',
      keys: [
        'merchant_name',
        'application_name',
        'provider',
        'provider_code',
        'execution_provider',
        'wallet',
        'wallet_country',
        'client_display',
        'customer_name',
        'customer_phone',
        'destination_display',
        'destination_account',
        'destination_name',
        'initiated_by_id',
      ],
    },
    {
      title: 'Amount',
      keys: [
        'amount_display',
        'amount',
        'currency_code',
        'wallet_currency',
        'refund_amount',
        'refundable_amount',
        'reserved_amount',
        'is_refunded',
        'can_be_refunded',
        'has_reserved_funds',
        'exchange_rate',
        'applied_rate',
        'conversion_fee',
      ],
    },
    {
      title: 'Status & timeline',
      keys: ['status', 'status_reason', 'created_at', 'updated_at', 'completed_at'],
    },
  ];

  /** Text for any payload value, flattening composite / stacked / choice / amount cells. */
  cell(v: unknown): string {
    if (v === null || v === undefined || v === '') {
      return '—';
    }
    if (typeof v === 'boolean') {
      return v ? 'Yes' : 'No';
    }
    if (typeof v !== 'object') {
      return String(v);
    }
    const o = v as Record<string, unknown>;
    const join = (...parts: unknown[]): string =>
      parts
        .map((p) => (p === null || p === undefined ? '' : String(p)))
        .filter((p) => p !== '')
        .join(' · ') || '—';
    if ('primary' in o) {
      return join(o['primary'], o['secondary'], o['tertiary']);
    }
    if ('top' in o || 'bottom' in o) {
      return join(o['top'], o['bottom']);
    }
    if ('title' in o && 'value' in o) {
      return String(o['title'] ?? o['value']);
    }
    if ('amount' in o && 'currency' in o) {
      return join(o['amount'], o['currency']);
    }
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }

  /** Grouped blocks for the Details tab; leftover fields land in a type-specific block. */
  detailGroups(): { title: string; fields: { key: string; label: string; value: unknown }[] }[] {
    const root = this.rootPayload();
    if (!root) {
      return [];
    }
    const used = new Set<string>(TransactionHubComponent.HIDDEN_FIELDS);
    const present = (k: string): boolean => k in root && root[k] !== undefined && root[k] !== null && root[k] !== '';
    const groups = TransactionHubComponent.FIELD_GROUPS.map((g) => {
      const fields = g.keys
        .filter((k) => present(k) || k === 'status')
        .map((k) => {
          used.add(k);
          return { key: k, label: this.formatKey(k), value: root[k] };
        });
      return { title: g.title, fields };
    });
    const rest = Object.keys(root)
      .filter((k) => !used.has(k) && present(k))
      .map((k) => ({ key: k, label: this.formatKey(k), value: root[k] }));
    if (rest.length) {
      groups.push({ title: this.typeBlockTitle(), fields: rest });
    }
    return groups.filter((g) => g.fields.length > 0);
  }

  typeBlockTitle(): string {
    const titles: Record<string, string> = {
      deposits: 'Deposit details',
      payouts: 'Payout details',
      refunds: 'Refund details',
      remittances: 'Remittance details',
      settlements: 'Settlement details',
      'direct-debits': 'Mandate details',
      'batch-disbursements': 'Batch details',
    };
    return titles[this.entityConfig.key] ?? 'More details';
  }

  statusText(v: unknown): string {
    const o = this.asRecord(v);
    return o ? this.str(o['value'] ?? o['title']) : this.str(v);
  }

  setTab(t: 'details' | 'webhooks'): void {
    this.tab.set(t);
  }

  webhookCount(): number {
    return this.asArray(this.hub()?.['webhooks']).length;
  }

  /** Expand / collapse a webhook row and load its full event (payload, headers, response). */
  toggleWebhook(eventId: string): void {
    if (this.openWebhookId() === eventId) {
      this.openWebhookId.set(null);
      this.webhookDetail.set(null);
      return;
    }
    this.openWebhookId.set(eventId);
    this.webhookDetail.set(null);
    this.webhookDetailError.set(null);
    this.webhookDetailLoading.set(true);
    this.webhookApi.getWebhookEvent(eventId).subscribe({
      next: (ev) => {
        this.webhookDetail.set(ev);
        this.webhookDetailLoading.set(false);
      },
      error: (err) => {
        this.webhookDetailError.set(err?.error?.detail ?? err?.message ?? 'Failed to load webhook event.');
        this.webhookDetailLoading.set(false);
      },
    });
  }

  json(v: unknown): string {
    if (v === null || v === undefined || v === '') {
      return '—';
    }
    if (typeof v === 'string') {
      try {
        return JSON.stringify(JSON.parse(v), null, 2);
      } catch {
        return v;
      }
    }
    try {
      return JSON.stringify(v, null, 2);
    } catch {
      return String(v);
    }
  }

  /** Scalar + shallow fields for main grid (avoid dumping nested JSON as one cell). */
  coreFieldEntries(): [string, unknown][] {
    const root = this.rootPayload();
    if (!root) {
      return [];
    }
    const priority = [
      'status',
      'amount',
      'currency',
      'currency_code',
      'provider_code',
      'provider_name',
      'internal_reference',
      'external_reference',
      'merchant_reference',
      'destination_account',
      'destination_name',
      'created_at',
      'updated_at',
    ];
    const seen = new Set<string>();
    const out: [string, unknown][] = [];
    for (const k of priority) {
      if (k in root && root[k] !== undefined) {
        const val = root[k];
        if (val !== null && typeof val === 'object') {
          continue;
        }
        out.push([k, val]);
        seen.add(k);
      }
    }
    for (const k of Object.keys(root)) {
      if (seen.has(k) || k === 'metadata') {
        continue;
      }
      const val = root[k];
      if (val !== null && typeof val === 'object') {
        continue;
      }
      out.push([k, val]);
    }
    return out;
  }

  isBooleanValue(v: unknown): boolean {
    return typeof v === 'boolean';
  }

  formatScalar(v: unknown): string {
    if (v === null || v === undefined) {
      return '—';
    }
    if (typeof v === 'boolean') {
      return v ? 'Yes' : 'No';
    }
    return String(v);
  }

  shouldCopyField(key: string): boolean {
    return (
      key === 'internal_reference' ||
      key === 'external_reference' ||
      key === 'merchant_reference' ||
      key === 'provider_transaction_id'
    );
  }

  copyField(key: string, value: unknown): void {
    if (!this.shouldCopyField(key)) {
      return;
    }
    void navigator.clipboard.writeText(this.formatScalar(value));
  }

  getTransactionStatusClass(status: string): string {
    const map: Record<string, string> = {
      PENDING: 'bg-warning-bg text-warning-fg',
      QUEUED: 'bg-warning-bg text-warning-fg',
      RESERVED: 'bg-warning-bg text-warning-fg',
      PROCESSING: 'bg-info-bg text-info-fg',
      CONVERTING: 'bg-info-bg text-info-fg',
      SUCCESS: 'bg-success-bg text-success-fg',
      COMPLETED: 'bg-success-bg text-success-fg',
      FAILED: 'bg-danger-bg text-danger-fg',
      REFUNDED: 'bg-surface-muted text-fg-secondary',
      REVERSED: 'bg-surface-muted text-fg-secondary',
      PARTIALLY_COMPLETED: 'bg-warning-bg text-warning-fg',
    };
    return map[status] ?? 'bg-surface-muted text-fg-secondary';
  }

  boolPillClass(v: unknown): string {
    return v === true ? 'bg-success-bg text-success-fg' : 'bg-danger-bg text-danger-fg';
  }

  metadataJson(): string {
    const root = this.rootPayload();
    if (!root?.['metadata']) {
      return '';
    }
    try {
      return JSON.stringify(root['metadata'], null, 2);
    } catch {
      return String(root['metadata']);
    }
  }

  submitRefund(): void {
    const reason = this.refundReason.trim();
    const password = this.refundPassword;
    if (!reason || !password) {
      return;
    }
    this.refundError.set(null);
    this.actionBusy.set(true);
    const amount = Number(this.refundAmount.replace(',', '.'));
    const left = Number(this.refundableAmount());
    if (!Number.isFinite(amount) || amount <= 0 || (this.refundableAmount() !== '' && amount > left)) {
      this.refundError.set('Enter an amount greater than 0 and not above the refundable balance.');
      return;
    }
    this.hubApi
      .postRefundFromHub(this.entityConfig.listPath, this.id, {
        reason,
        password,
        amount: amount.toFixed(2),
        reason_code: this.refundCode,
      })
      .subscribe({
      next: () => {
        this.actionBusy.set(false);
        this.closeRefundModal();
        this.load();
      },
      error: (err) => {
        this.refundError.set(err?.error?.detail ?? err?.message ?? 'Refund failed.');
        this.actionBusy.set(false);
      },
    });
  }

  checkStatus(): void {
    if (!this.canCheckStatus()) {
      return;
    }
    const reference = this.str(this.rootPayload()?.['internal_reference']);
    if (!reference) {
      this.error.set('Cannot check status: missing internal reference.');
      return;
    }
    this.error.set(null);
    this.actionBusy.set(true);
    this.hubApi
      .postStatusCheckFromHub(this.entityConfig.listPath, this.id, { reference })
      .subscribe({
      next: () => {
        this.actionBusy.set(false);
        this.load();
      },
      error: (err) => {
        this.error.set(err?.error?.detail ?? err?.message ?? 'Status check failed.');
        this.actionBusy.set(false);
      },
    });
  }

  resendWebhookRow(eventId: string): void {
    this.rowBusy.set(eventId);
    this.error.set(null);
    this.webhookApi.resendWebhook(eventId).subscribe({
      next: (_res: RetryResponse) => {
        this.rowBusy.set(null);
        this.load();
      },
      error: (err) => {
        this.error.set(err?.error?.error ?? err?.message ?? 'Resend failed.');
        this.rowBusy.set(null);
      },
    });
  }

  retryWebhookRow(eventId: string): void {
    this.rowBusy.set(eventId);
    this.error.set(null);
    this.webhookApi.retryWebhook(eventId).subscribe({
      next: (_res: RetryResponse) => {
        this.rowBusy.set(null);
        this.load();
      },
      error: (err) => {
        this.error.set(err?.error?.error ?? err?.message ?? 'Retry failed.');
        this.rowBusy.set(null);
      },
    });
  }

  canResendRow(w: Record<string, unknown>): boolean {
    const s = w['status'];
    return s === 'FAILED' || s === 'RETRYING';
  }

  canRetryRow(w: Record<string, unknown>): boolean {
    const s = w['status'];
    return s === 'FAILED' || s === 'RETRYING';
  }

  webhookDetailUrl(wid: string): (string | number | boolean)[] {
    return this.shellRoutes.commands('manage', 'webhook-events', wid);
  }

  walletDetailUrl(wid: string): (string | number | boolean)[] {
    return this.shellRoutes.commands('manage', 'wallets', wid);
  }

  isWebhookFailure(w: Record<string, unknown>): boolean {
    const s = this.str(w['status']);
    return s === 'FAILED' || s === 'RETRYING';
  }

  /** Prefer status_message; fall back to HTTP response code for failures. */
  webhookFailureDetail(w: Record<string, unknown>): string {
    const msg = this.str(w['status_message'] ?? '').trim();
    if (msg) {
      return msg;
    }
    const code = w['forward_response_code'];
    if (code !== null && code !== undefined && String(code) !== '') {
      return `Forward response: HTTP ${String(code)}`;
    }
    return '';
  }

  journalLineSummary(lines: unknown): string {
    if (!Array.isArray(lines) || !lines.length) {
      return '—';
    }
    return lines
      .map((row) => {
        const r = row as Record<string, unknown>;
        return `${this.str(r['currency_code'])} ${this.str(r['amount'])} ${this.str(r['side'])} (${this.str(r['account_code'])})`;
      })
      .join(' · ');
  }

  asArray(v: unknown): unknown[] {
    return Array.isArray(v) ? v : [];
  }

  asRecord(v: unknown): Record<string, unknown> | null {
    return v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
  }

  formatKey(key: string): string {
    return key.replace(/_/g, ' ');
  }

  getStatusClass(status: string): string {
    const map: Record<string, string> = {
      PENDING: 'bg-warning-bg text-warning-fg',
      PROCESSING: 'bg-info-bg text-info-fg',
      FORWARDED: 'bg-success-bg text-success-fg',
      FAILED: 'bg-danger-bg text-danger-fg',
      RETRYING: 'bg-warning-bg text-warning-fg',
    };
    return map[status] ?? 'bg-surface-muted text-fg-secondary';
  }

  getEventTypeClass(type: string): string {
    const map: Record<string, string> = {
      DEPOSIT: 'bg-success-bg text-success-fg',
      PAYOUT: 'bg-info-bg text-info-fg',
      REMITTANCE: 'bg-row-hover text-brand',
      REFUND: 'bg-danger-bg text-danger-fg',
      STATUS_UPDATE: 'bg-surface-muted text-fg-secondary',
      DIRECT_DEBIT: 'bg-success-bg text-success-fg',
      DEPOSIT_PROFILE: 'bg-success-bg text-success-fg',
      SETTLEMENT: 'bg-success-bg text-success',
    };
    return map[type] ?? 'bg-surface-muted text-fg-secondary';
  }

  copyToClipboard(value: string): void {
    void navigator.clipboard.writeText(value);
  }

  formatFeeAmount(amount: string, currency: string): string {
    return formatHumanAmount(amount, currency);
  }
}
